import type { PackRow, PackSearchHit } from '../types/apogee';

/**
 * Resolve Apogee internal id from a search hit (`_source.apogee_id`, or ES `_id` when numeric).
 * `_id` matches `apogee_id` per index contract; only accept all-digit `_id` strings for safety.
 */
export function apogeeIdFromSearchHit(hit: PackSearchHit): number | null {
  const fromSource = hit._source.apogee_id;
  if (typeof fromSource === 'number' && Number.isFinite(fromSource)) return fromSource;
  if (hit._id != null && /^\d+$/.test(String(hit._id))) return Number(hit._id);
  return null;
}

/**
 * Map a search hit to a `PackRow` for cards/tables. Returns null only when no internal
 * `apogee_id` can be resolved. Under CF-primary identity `mpi_id` may be absent (null).
 */
export function packRowFromSearchHit(hit: PackSearchHit): PackRow | null {
  const s = hit._source;
  const apogeeId = apogeeIdFromSearchHit(hit);
  if (apogeeId == null) return null;
  const mpiId = typeof s.mpi_id === 'number' ? s.mpi_id : null;

  const hasModrinth =
    (s.modrinth_project_id != null && String(s.modrinth_project_id).length > 0) ||
    (typeof s.modrinth_slug === 'string' && s.modrinth_slug.length > 0) ||
    (typeof s.modrinth_name === 'string' && s.modrinth_name.length > 0);

  const modrinth = hasModrinth
    ? {
        project_id: String(s.modrinth_project_id ?? ''),
        slug: s.modrinth_slug ?? '',
        name: s.modrinth_name ?? '',
        author: s.modrinth_author,
        description: s.modrinth_description,
        observed_at: s.modrinth_observed_at ?? '',
      }
    : undefined;

  const curseforge =
    typeof s.curseforge_mod_id === 'number'
      ? {
          cf_mod_id: s.curseforge_mod_id,
          author: s.curseforge_author,
          description: s.curseforge_description,
          observed_at: s.curseforge_observed_at ?? '',
        }
      : undefined;

  return {
    apogee_id: apogeeId,
    mpi_id: mpiId,
    cf_mod_id: typeof s.curseforge_mod_id === 'number' ? s.curseforge_mod_id : null,
    slug: s.slug ?? '',
    name: s.name ?? 'Untitled pack',
    summary: s.summary ?? '',
    thumbnail_url: s.thumbnail_url ?? null,
    first_seen_at: s.first_seen_at ?? '1970-01-01T00:00:00.000Z',
    first_seen_download_count: s.first_seen_download_count ?? 0,
    download_count: s.download_count,
    popularity_rank: s.popularity_rank ?? undefined,
    latest_release_date: s.latest_release_date ?? null,
    last_modified: s.last_modified ?? null,
    last_updated: s.last_updated ?? null,
    snapshot_observed_at: s.snapshot_observed_at,
    first_release_at: s.first_release_at ?? null,
    modpack_index_page_url: s.mpi_page_url ?? null,
    external_page_url: s.mpi_external_url ?? null,
    modrinth: modrinth ?? undefined,
    curseforge: curseforge ?? undefined,
  };
}
